import React, { useCallback, useRef } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Placeholder from '@tiptap/extension-placeholder';
import TextAlign from '@tiptap/extension-text-align';
import Table from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableHeader from '@tiptap/extension-table-header';
import TableCell from '@tiptap/extension-table-cell';
import Image from '@tiptap/extension-image';
import { Box, Tooltip, IconButton, Divider } from '@mui/material';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter';
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight';
import FormatAlignJustifyIcon from '@mui/icons-material/FormatAlignJustify';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import TableChartIcon from '@mui/icons-material/TableChart';
import AddBoxIcon from '@mui/icons-material/AddBox';
import DeleteIcon from '@mui/icons-material/Delete';
import IndeterminateCheckBoxIcon from '@mui/icons-material/IndeterminateCheckBox';
import './RichTextEditor.scss';
 
interface RichTextEditorProps {
    value?: string;
    onChange?: (content: string) => void;
    placeholder?: string;
    className?: string;
    minHeight?: string | number;
    maxHeight?: string | number;
    showAlignment?: boolean;
}
 
// Custom inline Image extension for proper HTML parsing/rendering
const CustomImage = Image.extend({
  inline: true,
  group: 'inline',
  draggable: true,
 
  addAttributes() {
    return {
      src: { default: null },
      alt: { default: null },
      title: { default: null },
    };
  },
 
  parseHTML() {
    return [{ tag: 'img[src]' }];
  },
 
  renderHTML({ HTMLAttributes }: any) {
    return ['img', HTMLAttributes];
  },
});
 
const RichTextEditor: React.FC<RichTextEditorProps> = ({
    value = '',
    onChange,
    placeholder = 'Description',
    className = '',
    minHeight,
    maxHeight,
    showAlignment = false,
}) => {
    const fileInputRef = useRef<HTMLInputElement>(null);
 
    const editor = useEditor({
        extensions: [
            StarterKit,
            Underline,
            TextAlign.configure({
                types: ['heading', 'paragraph'],
            }),
            Placeholder.configure({
                placeholder,
            }),
            Table.configure({ resizable: true }),
            TableRow,
            TableHeader,
            TableCell,
            CustomImage,
        ],
        content: value,
        onUpdate: ({ editor }: { editor: any }) => {
            onChange?.(editor.getHTML());
        },
    });
 
 
 
    const toggleBold = useCallback(() => {
        editor?.chain().focus().toggleBold().run();
    }, [editor]);
 
    const toggleItalic = useCallback(() => {
        editor?.chain().focus().toggleItalic().run();
    }, [editor]);
 
    const toggleUnderline = useCallback(() => {
        editor?.chain().focus().toggleUnderline().run();
    }, [editor]);
 
    const toggleBulletList = useCallback(() => {
        editor?.chain().focus().toggleBulletList().run();
    }, [editor]);
 
    const toggleOrderedList = useCallback(() => {
        editor?.chain().focus().toggleOrderedList().run();
    }, [editor]);
 
    const setAlignment = useCallback((alignment: string) => {
        editor?.chain().focus().setTextAlign(alignment).run();
    }, [editor]);
 
    const insertImageFromFile = (file: File) => {
        const reader = new FileReader();
        reader.onload = () => {
            const base64 = reader.result;
            if (!editor) return;
 
            if (!editor.can().insertContent({ type: 'image', attrs: { src: base64 } })) {
                editor.chain().focus().setParagraph().run();
            }
 
            editor
                .chain()
                .focus()
                .setImage({ src: base64 as string })
                .insertContent({ type: 'paragraph', content: [] })
                .command(({ tr, state, dispatch }: any) => {
                    const newPos = tr.selection.to;
                    dispatch?.(
                        tr.setSelection(state.selection.constructor.near(tr.doc.resolve(newPos), 1))
                    );
                    return true;
                })
                .run();
 
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        };
        reader.readAsDataURL(file);
    };
 
    const handleAttachClick = () => {
        fileInputRef.current?.click();
    };
 
    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            insertImageFromFile(file);
        }
    };
 
    if (!editor) {
        return null;
    }
 
    return (
        <Box 
            className={`rich-text-editor-container ${className} ${editor.isFocused ? 'focused' : ''}`}
            sx={{
                minHeight: minHeight || '150px',
                maxHeight: maxHeight || '400px',
            }}
        >
            <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={handleFileChange}
            />
            <Box className="editor-content-area editor-content-area-wrapper" sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                <EditorContent editor={editor} />
            </Box>
 
            <Box className="editor-toolbar">
                <Box className="toolbar-actions">
                    <Tooltip title="Bold (Ctrl+B)" arrow>
                        <IconButton
                            size="small"
                            onClick={toggleBold}
                            className={`toolbar-btn ${editor.isActive('bold') ? 'is-active' : ''}`}
                            disableRipple
                            sx={{ fontWeight: 700, fontSize: '16px', fontFamily: 'Montserrat' }}
                        >
                            B
                        </IconButton>
                    </Tooltip>
 
                    <Tooltip title="Underline (Ctrl+U)" arrow>
                        <IconButton
                            size="small"
                            onClick={toggleUnderline}
                            className={`toolbar-btn ${editor.isActive('underline') ? 'is-active' : ''}`}
                            disableRipple
                            sx={{ textDecoration: 'underline', fontSize: '16px', fontFamily: 'Montserrat' }}
                        >
                            U
                        </IconButton>
                    </Tooltip>
 
                    <Tooltip title="Italic (Ctrl+I)" arrow>
                        <IconButton
                            size="small"
                            onClick={toggleItalic}
                            className={`toolbar-btn ${editor.isActive('italic') ? 'is-active' : ''}`}
                            disableRipple
                            sx={{ fontStyle: 'italic', fontSize: '16px', fontFamily: 'serif' }}
                        >
                            I
                        </IconButton>
                    </Tooltip>
 
                    <Tooltip title="Bullet List" arrow>
                        <IconButton
                            size="small"
                            onClick={toggleBulletList}
                            className={`toolbar-btn ${editor.isActive('bulletList') ? 'is-active' : ''}`}
                            disableRipple
                        >
                            <FormatListBulletedIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
 
                    <Tooltip title="Ordered List" arrow>
                        <IconButton
                            size="small"
                            onClick={toggleOrderedList}
                            className={`toolbar-btn ${editor.isActive('orderedList') ? 'is-active' : ''}`}
                            disableRipple
                        >
                            <FormatListNumberedIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
 
                    <Tooltip title="Insert Table">
                        <IconButton
                        size="small"
                        onClick={() => editor.chain().focus().insertTable({ rows: 2, cols: 2, withHeaderRow: true }).run()}
                        className={`toolbar-btn ${editor.isActive('table') ? 'is-active' : ''}`}
                        disableRipple
                        >
                            <TableChartIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
 
                    {editor.isActive('table') && (
                        <>
                            <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 1, borderColor: '#E3E8EF' }} />
                            
                            <Tooltip title="Add Row" arrow>
                                <IconButton size="small" onClick={() => editor.chain().focus().addRowAfter().run()} className="toolbar-btn" disableRipple>
                                    <AddBoxIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                            
                            <Tooltip title="Add Column" arrow>
                                <IconButton size="small" onClick={() => editor.chain().focus().addColumnAfter().run()} className="toolbar-btn" disableRipple>
                                    <AddBoxIcon fontSize="small" style={{ transform: 'rotate(90deg)' }} />
                                </IconButton>
                            </Tooltip>
                            
                            <Tooltip title="Delete Row" arrow>
                                <IconButton size="small" onClick={() => editor.chain().focus().deleteRow().run()} className="toolbar-btn" disableRipple>
                                    <IndeterminateCheckBoxIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                            
                            <Tooltip title="Delete Column" arrow>
                                <IconButton size="small" onClick={() => editor.chain().focus().deleteColumn().run()} className="toolbar-btn" disableRipple>
                                    <IndeterminateCheckBoxIcon fontSize="small" style={{ transform: 'rotate(90deg)' }} />
                                </IconButton>
                            </Tooltip>
 
                            <Tooltip title="Delete Table" arrow>
                                <IconButton size="small" onClick={() => editor.chain().focus().deleteTable().run()} className="toolbar-btn" disableRipple>
                                    <DeleteIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </>
                    )}
 
                    {showAlignment && (
                        <>
                            <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 1, borderColor: '#E3E8EF' }} />
 
                            <Tooltip title="Align Left" arrow>
                                <IconButton
                                    size="small"
                                    onClick={() => setAlignment('left')}
                                    className={`toolbar-btn ${editor.isActive({ textAlign: 'left' }) ? 'is-active' : ''}`}
                                    disableRipple
                                >
                                    <FormatAlignLeftIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
 
                            <Tooltip title="Align Center" arrow>
                                <IconButton
                                    size="small"
                                    onClick={() => setAlignment('center')}
                                    className={`toolbar-btn ${editor.isActive({ textAlign: 'center' }) ? 'is-active' : ''}`}
                                    disableRipple
                                >
                                    <FormatAlignCenterIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
 
                            <Tooltip title="Align Right" arrow>
                                <IconButton
                                    size="small"
                                    onClick={() => setAlignment('right')}
                                    className={`toolbar-btn ${editor.isActive({ textAlign: 'right' }) ? 'is-active' : ''}`}
                                    disableRipple
                                >
                                    <FormatAlignRightIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
 
                            <Tooltip title="Justify" arrow>
                                <IconButton
                                    size="small"
                                    onClick={() => setAlignment('justify')}
                                    className={`toolbar-btn ${editor.isActive({ textAlign: 'justify' }) ? 'is-active' : ''}`}
                                    disableRipple
                                >
                                    <FormatAlignJustifyIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </>
                    )}
                </Box>
 
                <Tooltip title="Attach Files" arrow>
                    <IconButton className="attachment-btn" size="small" disableRipple onClick={handleAttachClick}>
                        <AttachFileIcon fontSize="small" style={{ transform: 'rotate(45deg)' }} />
                    </IconButton>
                </Tooltip>
            </Box>
        </Box>
    );
};
 
export default RichTextEditor;